import React, { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../../../Context'
import { AuthContextApi } from '../../../Context/api'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import './index.css'
import {
    PlusIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    TruckIcon,
    UserIcon,
    PhoneIcon,
    EnvelopeIcon,
    IdentificationIcon,
    MapPinIcon,
    ArrowLeftIcon,
    ClockIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline'

// Funções de formatação
const formatTelefone = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '')

    // Limita a 11 dígitos (DDD + 9 dígitos)
    const limitedNumbers = numbers.slice(0, 11)

    // Aplica a máscara de telefone
    if (limitedNumbers.length <= 10) {
        return limitedNumbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    } else {
        return limitedNumbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
}

const formatCPFCNPJ = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '')

    // Limita a 14 dígitos (máximo para CNPJ)
    const limitedNumbers = numbers.slice(0, 14)

    // Aplica a máscara baseada no tamanho
    if (limitedNumbers.length <= 11) {
        // CPF
        return limitedNumbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else {
        // CNPJ
        return limitedNumbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }
}

const formatCEP = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '')

    // Limita a 8 dígitos
    const limitedNumbers = numbers.slice(0, 8)

    // Aplica a máscara de CEP
    return limitedNumbers.replace(/(\d{5})(\d{3})/, '$1-$2')
}

const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const Fornecedores = () => {
    const { states, setters } = useContext(AuthContext)
    const {
        getFornecedoresHistorico,
        getFornecedores,
        getEstatisticasFornecedores,
        postFornecedores,
        putFornecedores,
    } = useContext(AuthContextApi)
    const navigate = useNavigate()

    // Função para retornar ao painel de entidades
    const navigateToEntidades = () => {
        navigate(-1)
    }

    // Estados locais
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome')
    const [selectedSortField, setSelectedSortField] = useState('nome')
    const [sortDirection, setSortDirection] = useState('asc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [selectedEntidade, setSelectedEntidade] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Estados do formulário
    const [entidadeNome, setEntidadeNome] = useState('')
    const [entidadeNumero, setEntidadeNumero] = useState('')
    const [entidadeEmail, setEntidadeEmail] = useState('')
    const [entidadeTelefone, setEntidadeTelefone] = useState('')
    const [entidadeCpfCnpj, setEntidadeCpfCnpj] = useState('')
    const [entidadeCep, setEntidadeCep] = useState('')

    // Estados para histórico de fornecedor
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false)
    const [historicoPage, setHistoricoPage] = useState(0)
    const [historicoHasMore, setHistoricoHasMore] = useState(true)
    const [historicoLoading, setHistoricoLoading] = useState(false)
    const [currentFornecedorId, setCurrentFornecedorId] = useState(null)

    // Função para carregar fornecedores usando a rota específica
    const loadFornecedores = async () => {
        const query = new URLSearchParams({
            page: states.page,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        }).toString()

        await getFornecedores(query)
    }

    // Função para buscar
    const handleSearch = () => {
        setters.setPage(0)
        loadFornecedores()
    }

    // Função para busca dinâmica com debounce
    const handleSearchChange = (value) => {
        setSearchTerm(value)
    }

    // useEffect para busca dinâmica com debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm !== '') {
                setters.setPage(0)
                loadFornecedores()
            }
        }, 500)

        return () => clearTimeout(timeoutId)
    }, [searchTerm])

    // Função para limpar busca
    const handleClear = () => {
        setSearchTerm('')
        setSelectedFilterField('nome')
        setSelectedSortField('nome')
        setSortDirection('asc')
        setters.setPage(0)
        loadFornecedores()
    }

    // Função para próxima página
    const handleNext = () => {
        setters.setPage(states.page + 1)
    }

    // Função para página anterior
    const handlePrevious = () => {
        if (states.page >= 0) {
            states.page > 0 && setters.setPage(states.page - 1)
        }
    }

    // Função para abrir modal
    const openModal = (entidade = null) => {
        if (entidade) {
            setIsEditMode(true)
            setSelectedEntidade(entidade)
            setEntidadeNome(entidade.nome || '')
            setEntidadeNumero(entidade.numero || '')
            setEntidadeEmail(entidade.email || '')
            setEntidadeTelefone(entidade.telefone || '')
            setEntidadeCpfCnpj(entidade.cpf_cnpj || '')
            setEntidadeCep(entidade.cep || '')
        } else {
            setIsEditMode(false)
            setSelectedEntidade({})
            setEntidadeNome('')
            setEntidadeNumero('')
            setEntidadeEmail('')
            setEntidadeTelefone('')
            setEntidadeCpfCnpj('')
            setEntidadeCep('')
        }
        setIsModalOpen(true)
    }

    // Função para fechar modal
    const closeModal = () => {
        setIsModalOpen(false)
        setIsEditMode(false)
        setSelectedEntidade({})
        setEntidadeNome('')
        setEntidadeNumero('')
        setEntidadeEmail('')
        setEntidadeTelefone('')
        setEntidadeCpfCnpj('')
        setEntidadeCep('')
        setIsSubmitting(false)
    }

    // Função para salvar fornecedor
    const handleSaveFornecedor = async () => {
        if (!entidadeNome.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        if (!entidadeEmail.trim()) {
            toast.error('Email é obrigatório')
            return
        }

        if (!entidadeTelefone.trim()) {
            toast.error('Telefone é obrigatório')
            return
        }

        if (!entidadeCpfCnpj.trim()) {
            toast.error('CPF/CNPJ é obrigatório')
            return
        }

        setIsSubmitting(true)

        const fornecedorData = {
            nome: entidadeNome.trim(),
            email: entidadeEmail.trim(),
            telefone: entidadeTelefone.trim(),
            cpf_cnpj: entidadeCpfCnpj.trim()
        }

        if (isEditMode) {
            fornecedorData.id = selectedEntidade.id
            const success = await putFornecedores(fornecedorData)
            if (success) {
                closeModal()
                setTimeout(() => {
                    loadFornecedores()
                }, 500)
            }
        } else {
            const success = await postFornecedores(fornecedorData)
            if (success) {
                closeModal()
                setTimeout(() => {
                    loadFornecedores()
                }, 500)
            }
        }

        setIsSubmitting(false)
    }

    // Função para abrir histórico de fornecedor
    const openHistoricoFornecedor = async (fornecedorId) => {
        setCurrentFornecedorId(fornecedorId)
        setHistoricoPage(0)
        setHistoricoHasMore(true)
        await loadHistoricoPage(fornecedorId, 0, true)
        setIsHistoricoOpen(true)
    }

    // Função para carregar página do histórico
    const loadHistoricoPage = async (fornecedorId, page, reset = false) => {
        if (!historicoLoading) {
            setHistoricoLoading(true)
            try {
                const query = new URLSearchParams({
                    page: page,
                    per_page: 10
                }).toString()

                await getFornecedoresHistorico(fornecedorId, query)

                // Verifica se há mais dados baseado no tamanho da resposta
                const currentData = states.fornecedoresHistorico || []
                setHistoricoPage(page)
            } catch (error) {
                console.error('Erro ao carregar histórico do fornecedor:', error)
                toast.error('Erro ao carregar histórico do fornecedor')
            } finally {
                setHistoricoLoading(false)
            }
        }
    }

    // Função para carregar mais histórico
    const loadMoreHistorico = () => {
        if (historicoHasMore && !historicoLoading && currentFornecedorId) {
            loadHistoricoPage(currentFornecedorId, historicoPage + 1, false)
        }
    }

    // Função para fechar modal de histórico
    const closeHistoricoModal = () => {
        setIsHistoricoOpen(false)
        setCurrentFornecedorId(null)
        setHistoricoPage(0)
        setHistoricoHasMore(true)
        setHistoricoLoading(false)
    }

    // useEffect para monitorar mudanças nas variáveis de query
    useEffect(() => {
        if (states.page >= 0) {
            loadFornecedores()
        }
    }, [states.page, recordsPerPage])

    // useEffect para monitorar mudanças nos filtros
    useEffect(() => {
        setters.setPage(0)
        loadFornecedores()
    }, [selectedSortField, sortDirection])

    // useEffect para limpeza ao desmontar
    useEffect(() => {
        getEstatisticasFornecedores()
        loadFornecedores()
        return () => {
            setters.setPage(0)
            setters.setIsLoading(false)
            setters.setDataFornecedores([])
            setters.setDataEstatisticaFornecedores([])
        }
    }, [])


    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/30">
            {/* Header Artístico */}
            <div className="relative bg-gradient-to-br from-green-50/80 via-white/80 to-green-50/80 backdrop-blur-sm border-b border-gradient-to-r from-green-200/50 to-green-200/50 sticky top-0 z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-green-500/5"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/10 to-green-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center">
                            <button
                                onClick={navigateToEntidades}
                                className="group mr-6 p-3 rounded-2xl bg-white/20 backdrop-blur-sm border border-green-200/50 hover:bg-green-100/50 transition-all duration-300 transform hover:scale-105"
                            >
                                <ArrowLeftIcon className="w-6 h-6 text-green-700 group-hover:text-green-800 transition-colors duration-200" />
                            </button>
                            <div>
                                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-green-800 to-green-800 bg-clip-text text-transparent">
                                    Fornecedores
                                </h1>
                                <p className="mt-3 text-lg text-gray-600 font-medium">
                                    Gerencie os fornecedores do sistema
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 sm:mt-0">
                            <button
                                onClick={() => openModal()}
                                className="group relative inline-flex items-center px-8 py-4 border border-transparent rounded-2xl shadow-xl text-sm font-semibold text-white bg-gradient-to-r from-green-600 via-green-700 to-green-700 hover:from-green-700 hover:via-green-800 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-105"
                            >
                                <div className="relative z-10 flex items-center">
                                    <PlusIcon className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" />
                                    <span className="text-base font-bold">Novo Fornecedor</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-green-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conteúdo Principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Cards de Estatísticas - Design Artístico */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
                    {states.dataEstatisticaFornecedores && Array.isArray(states.dataEstatisticaFornecedores) && states.dataEstatisticaFornecedores.length > 0 && states.dataEstatisticaFornecedores.map((card, index) => {
                        return (
                            <div key={card.title} className="relative bg-gradient-to-br from-green-50 via-white to-green-50/30 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-green-500/5"></div>
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-400/10 to-green-400/10 rounded-full -translate-y-12 translate-x-12"></div>
                                <div className="relative p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="p-3 rounded-2xl bg-gradient-to-r from-green-500 via-green-600 to-green-700 shadow-lg">
                                            <TruckIcon className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-green-800 to-green-800 bg-clip-text text-transparent">
                                                {card.value}
                                            </div>
                                        </div>
                                    </div>
                                    <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 via-green-800 to-green-700 bg-clip-text text-transparent">
                                        {card.title}
                                    </h3>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Formulário de Busca - Design Artístico */}
                <div className="relative bg-gradient-to-br from-green-50/80 via-white/80 to-green-50/80 backdrop-blur-sm rounded-3xl shadow-lg border border-green-100/50 mb-8 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-green-500/5"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/10 to-green-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="relative p-6">
                        <div className="flex items-center mb-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-r from-green-500 via-green-600 to-green-600 shadow-lg mr-4">
                                <MagnifyingGlassIcon className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-green-800 to-green-800 bg-clip-text text-transparent">
                                Pesquisa Avançada
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Termo de Busca</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch()
                                        }
                                    }}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    placeholder="Digite para buscar..."
                                    className="w-full px-4 py-3 border border-green-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Campo de Filtro</label>
                                <select
                                    value={selectedFilterField}
                                    onChange={(e) => setSelectedFilterField(e.target.value)}
                                    className="w-full px-4 py-3 border border-green-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value="cpf_cnpj">CPF/CNPJ</option>
                                    <option value="data_atualizacao">Data de Atualização</option>
                                    <option value="data_criacao">Data de Criação</option>
                                    <option value="email">Email</option>
                                    <option value="nome">Nome</option>
                                    <option value="telefone">Telefone</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Campo de Ordenação</label>
                                <select
                                    value={selectedSortField}
                                    onChange={(e) => setSelectedSortField(e.target.value)}
                                    className="w-full px-4 py-3 border border-green-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value="data_atualizacao">Data de Atualização</option>
                                    <option value="data_criacao">Data de Criação</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Direção da Ordenação</label>
                                <select
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value)}
                                    className="w-full px-4 py-3 border border-green-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value="asc">Crescente (A-Z)</option>
                                    <option value="desc">Decrescente (Z-A)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Registros por Página</label>
                                <select
                                    value={recordsPerPage}
                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                    className="w-full px-4 py-3 border border-green-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value={5}>5 registros</option>
                                    <option value={10}>10 registros</option>
                                    <option value={20}>20 registros</option>
                                    <option value={50}>50 registros</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={handleSearch}
                                className="flex-1 group relative inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 via-green-700 to-green-700 hover:from-green-700 hover:via-green-800 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-105"
                            >
                                <div className="relative z-10 flex items-center">
                                    <MagnifyingGlassIcon className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                    <span>Buscar</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-green-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>

                            <button
                                onClick={handleClear}
                                className="flex-1 group relative inline-flex items-center justify-center px-6 py-3 border border-green-300 rounded-2xl shadow-lg text-sm font-semibold text-green-700 bg-gradient-to-r from-green-50 to-green-50 hover:from-green-100 hover:to-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-105"
                            >
                                <div className="relative z-10 flex items-center">
                                    <XMarkIcon className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                    <span>Limpar</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-green-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Lista de Fornecedores - Design Artístico */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50 overflow-hidden">
                    {/* Cabeçalho da Tabela */}
                    <div className="bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-4 border-b border-green-200/50">
                        <div className="grid grid-cols-7 gap-4 text-sm font-semibold text-gray-700">
                            <div className="text-center">Ações</div>
                            <div className="text-center">Nome</div>
                            <div className="text-center">Email</div>
                            <div className="text-center">Telefone</div>
                            <div className="text-center">CPF/CNPJ</div>
                            <div className="text-center">Criado em</div>
                            <div className="text-center">Status</div>
                        </div>
                    </div>

                    {/* Lista de Fornecedores */}
                    <div className="divide-y divide-gray-200/50">
                        {states.dataFornecedores && Array.isArray(states.dataFornecedores) && states.dataFornecedores.length > 0 && states.dataFornecedores.map((entidade) => (
                            <div
                                key={entidade.id}
                                className="px-6 py-4 hover:bg-green-50/30 transition-colors duration-200 cursor-pointer group"
                                onClick={() => openModal(entidade)}
                            >
                                <div className="grid grid-cols-7 gap-4 items-center">
                                    {/* Ações */}
                                    <div className="flex items-center justify-center space-x-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                openModal(entidade)
                                            }}
                                            className="p-2 rounded-lg hover:bg-green-100 transition-colors duration-200 group-hover:scale-110"
                                            title="Editar Fornecedor"
                                        >
                                            <PencilIcon className="w-4 h-4 text-green-600" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                openHistoricoFornecedor(entidade.id)
                                            }}
                                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 group-hover:scale-110"
                                            title="Ver Histórico"
                                        >
                                            <ClockIcon className="w-4 h-4 text-gray-600" />
                                        </button>
                                    </div>

                                    {/* Nome do fornecedor */}
                                    <div className="text-center">
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 via-green-600 to-green-700 rounded-lg flex items-center justify-center shadow-sm">
                                                <TruckIcon className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 text-center">
                                                {entidade.nome}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div className="text-center">
                                        <span className="text-sm text-gray-700">
                                            {entidade.email || '-'}
                                        </span>
                                    </div>

                                    {/* Telefone */}
                                    <div className="text-center">
                                        <span className="text-sm text-gray-700">
                                            {entidade.telefone ? formatTelefone(entidade.telefone) : '-'}
                                        </span>
                                    </div>

                                    {/* CPF/CNPJ */}
                                    <div className="text-center">
                                        <span className="text-sm text-gray-700">
                                            {entidade.cpf_cnpj ? formatCPFCNPJ(entidade.cpf_cnpj) : '-'}
                                        </span>
                                    </div>

                                    {/* Data de criação */}
                                    <div className="text-center">
                                        <span className="text-sm text-gray-700">
                                            {formatDate(entidade.data_criacao)}
                                        </span>
                                    </div>

                                    {/* Status */}
                                    <div className="text-center">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                                            Fornecedor
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mensagem quando não há fornecedores */}
                    {(!states.dataFornecedores || !Array.isArray(states.dataFornecedores) || states.dataFornecedores.length === 0) && (
                        <div className="text-center py-12">
                            <TruckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum fornecedor encontrado</h3>
                            <p className="text-gray-500">Clique em "Novo Fornecedor" para criar o primeiro fornecedor.</p>
                        </div>
                    )}
                </div>

                {/* Paginação */}
                {states.dataFornecedores && Array.isArray(states.dataFornecedores) && states.dataFornecedores.length > 0 && (
                    <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
                        <div className="text-sm text-gray-700">
                            Mostrando {((states.page - 1) * recordsPerPage) + 1} a {Math.min(states.page * recordsPerPage, states.dataFornecedores?.length || 0)} de {states.dataFornecedores?.length || 0} fornecedores
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={handlePrevious}
                                disabled={states.page === 0}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                            </button>
                            <span className="px-3 py-2 text-sm font-medium text-gray-700">
                                Página {states.page + 1}
                            </span>
                            <button
                                onClick={handleNext}
                                disabled={(states.dataFornecedores?.length || 0) < recordsPerPage}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Mensagem quando não há dados */}
                {(!states.dataFornecedores || !Array.isArray(states.dataFornecedores) || states.dataFornecedores.length === 0) && (
                    <div className="text-center py-12">
                        <TruckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum fornecedor encontrado</h3>
                        <p className="text-gray-500 mb-6">Comece criando seu primeiro fornecedor para ver os dados aqui.</p>
                        <button
                            onClick={() => openModal()}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Criar Fornecedor
                        </button>
                    </div>
                )}
            </div>

            {/* Modal de Adicionar/Editar Fornecedor */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                        {/* Header do Modal */}
                        <div className="relative shrink-0 bg-gradient-to-r from-green-600 via-green-700 to-green-700 rounded-t-3xl p-6">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-green-500/10 rounded-t-3xl"></div>
                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm mr-4">
                                        <TruckIcon className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-white">
                                            {isEditMode ? 'Editar Fornecedor' : 'Novo Fornecedor'}
                                        </h2>
                                        <p className="text-green-100 mt-1">
                                            {isEditMode ? 'Atualize os dados do fornecedor' : 'Preencha os dados para criar um novo fornecedor'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors duration-200"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Conteúdo do Modal */}
                        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
                            {/* Nome */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Nome do Fornecedor *
                                </label>
                                <input
                                    type="text"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveFornecedor()
                                        }
                                    }}
                                    value={entidadeNome}
                                    onChange={(e) => setEntidadeNome(e.target.value)}
                                    placeholder="Digite o nome do fornecedor"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveFornecedor()
                                        }
                                    }}
                                    value={entidadeEmail}
                                    onChange={(e) => setEntidadeEmail(e.target.value)}
                                    placeholder="Digite o email"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>

                            {/* Telefone */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Telefone *
                                </label>
                                <input
                                    type="tel"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveFornecedor()
                                        }
                                    }}
                                    value={entidadeTelefone}
                                    onChange={(e) => setEntidadeTelefone(formatTelefone(e.target.value))}
                                    placeholder="(11) 99999-9999"
                                    maxLength={15}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>

                            {/* CPF/CNPJ */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    CPF/CNPJ *
                                </label>
                                <input
                                    type="text"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveFornecedor()
                                        }
                                    }}
                                    value={entidadeCpfCnpj}
                                    onChange={(e) => setEntidadeCpfCnpj(formatCPFCNPJ(e.target.value))}
                                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                    maxLength={18}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>

                        </div>

                        {/* Footer do Modal */}
                        <div className="flex shrink-0 items-center justify-end space-x-4 border-t border-gray-200 p-6">
                            <button
                                onClick={closeModal}
                                className="px-6 py-3 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveFornecedor}
                                disabled={isSubmitting}
                                className="group relative inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 via-green-700 to-green-700 hover:from-green-700 hover:via-green-800 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="relative z-10 flex items-center">
                                    {isSubmitting ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    ) : (
                                        <PlusIcon className="w-4 h-4 mr-2" />
                                    )}
                                    <span>{isSubmitting ? 'Salvando...' : (isEditMode ? 'Atualizar' : 'Criar')}</span>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-green-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Histórico de Fornecedor */}
            {isHistoricoOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 z-50">
                    <div className="relative flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                        {/* Header do Modal */}
                        <div className="shrink-0 border-b border-gray-100 bg-white px-4 py-4 sm:px-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center min-w-0 flex-1">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                        <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                                            Histórico do Fornecedor
                                        </h2>
                                        <p className="text-xs sm:text-sm text-gray-500 truncate">
                                            Visualize todas as alterações realizadas neste fornecedor
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeHistoricoModal}
                                    className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-200 flex-shrink-0 ml-2"
                                >
                                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {/* Conteúdo do Histórico */}
                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
                            {states.fornecedoresHistorico && states.fornecedoresHistorico.length > 0 ? (
                                <div className="space-y-3 sm:space-y-4">
                                    {states.fornecedoresHistorico.map((item, index) => (
                                        <div key={item.id} className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                                            {/* Header do Item */}
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
                                                <div className="flex items-center min-w-0 flex-1">
                                                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${item.transformacao === 'Criação' ? 'bg-green-100' :
                                                        item.transformacao === 'Edição' ? 'bg-blue-100' :
                                                            'bg-gray-100'
                                                        }`}>
                                                        {item.transformacao === 'Criação' ? (
                                                            <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                                                        ) : item.transformacao === 'Edição' ? (
                                                            <PencilIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                                                        ) : (
                                                            <TrashIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                                                            {item.transformacao}
                                                        </h3>
                                                        <p className="text-xs text-gray-500 truncate">
                                                            por {item.user_email}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-left sm:text-right">
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(item.data_atualizacao).toLocaleDateString('pt-BR')}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        {new Date(item.data_atualizacao).toLocaleTimeString('pt-BR')}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Dados Atuais */}
                                            {item.dado_atual && (
                                                <div className="mb-3">
                                                    <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                        Dados Atuais
                                                    </h4>
                                                    <div className="bg-white rounded-lg p-2 sm:p-3 border border-gray-100">
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                                            {item.dado_atual.nome && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Nome</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{item.dado_atual.nome}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_atual.email && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Email</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{item.dado_atual.email}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_atual.telefone && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Telefone</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatTelefone(item.dado_atual.telefone)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_atual.cpf_cnpj && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">CPF/CNPJ</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatCPFCNPJ(item.dado_atual.cpf_cnpj)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_atual.data_criacao && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Criado em</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_atual.data_criacao)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_atual.data_atualizacao && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Atualizado em</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_atual.data_atualizacao)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_atual.deletado !== undefined && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Status</p>
                                                                    <p className="text-sm font-semibold text-gray-900">
                                                                        {item.dado_atual.deletado ? 'Deletado' : 'Ativo'}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Dados Anteriores (se houver) */}
                                            {item.dado_antigo && (
                                                <div>
                                                    <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                        Dados Anteriores
                                                    </h4>
                                                    <div className="bg-red-50 rounded-lg p-2 sm:p-3 border border-red-100">
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                                            {item.dado_antigo.nome && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Nome</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{item.dado_antigo.nome}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_antigo.email && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Email</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{item.dado_antigo.email}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_antigo.telefone && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Telefone</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatTelefone(item.dado_antigo.telefone)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_antigo.cpf_cnpj && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">CPF/CNPJ</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatCPFCNPJ(item.dado_antigo.cpf_cnpj)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_antigo.data_criacao && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Criado em</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_antigo.data_criacao)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_antigo.data_atualizacao && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Atualizado em</p>
                                                                    <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_antigo.data_atualizacao)}</p>
                                                                </div>
                                                            )}
                                                            {item.dado_antigo.deletado !== undefined && (
                                                                <div>
                                                                    <p className="text-xs font-medium text-gray-500">Status</p>
                                                                    <p className="text-sm font-semibold text-gray-900">
                                                                        {item.dado_antigo.deletado ? 'Deletado' : 'Ativo'}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Botão Carregar Mais */}
                                    {historicoHasMore && (
                                        <div className="flex justify-center mt-6">
                                            <button
                                                onClick={loadMoreHistorico}
                                                disabled={historicoLoading}
                                                className="group relative inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 via-green-700 to-green-700 hover:from-green-700 hover:via-green-800 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {historicoLoading ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                        Carregando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <PlusIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                                        Carregar Mais
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum histórico encontrado</h3>
                                    <p className="text-gray-500">Este fornecedor ainda não possui histórico de alterações.</p>
                                </div>
                            )}
                        </div>

                        {/* Footer do Modal */}
                        <div className="flex shrink-0 justify-end border-t border-gray-100 bg-gray-50 px-4 py-3 sm:px-6 sm:py-4">
                            <button
                                onClick={closeHistoricoModal}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Fornecedores
